// client/src/utils/placeholders.js - VERSÃO CORRIGIDA 100% LOCAL
/**
 * Utilitário para geração e gerenciamento de placeholders de imagens (FRONTEND)
 * Mantém consistência com o backend - SEM DEPENDÊNCIAS EXTERNAS
 */

class PlaceholderGenerator {
  static typeConfigs = {
    // Recursos e Serviços
    recurso: { bg: '007bff', text: 'ffffff', label: 'Recurso', icon: '📦' },
    servico: { bg: '28a745', text: 'ffffff', label: 'Serviço', icon: '🔧' },
    categoria: { bg: 'fd7e14', text: 'ffffff', label: 'Categoria', icon: '📂' },
    
    // Pessoas
    cliente: { bg: '6f42c1', text: 'ffffff', label: 'Cliente', icon: '👤' },
    usuario: { bg: '17a2b8', text: 'ffffff', label: 'Usuário', icon: '👨‍💼' },
    profissional: { bg: '20c997', text: 'ffffff', label: 'Profissional', icon: '👨‍⚕️' },
    
    // Agendamentos e Vendas
    agendamento: { bg: 'dc3545', text: 'ffffff', label: 'Agendamento', icon: '📅' },
    pedido: { bg: 'ffc107', text: '000000', label: 'Pedido', icon: '📝' },
    venda: { bg: '20c997', text: 'ffffff', label: 'Venda', icon: '💰' },
    
    // Financeiro
    pagamento: { bg: '6610f2', text: 'ffffff', label: 'Pagamento', icon: '💳' },
    contrato: { bg: '6f42c1', text: 'ffffff', label: 'Contrato', icon: '📄' },
    
    // Default
    default: { bg: 'cccccc', text: '333333', label: 'Imagem', icon: '🖼️' }
  };

  static avatarColors = [
    'FF6B6B', '4ECDC4', '45B7D1', '96CEB4', 'FFEAA7',
    'DDA0DD', '98D8C8', 'F7DC6F', 'BB8FCE', '85C1E9'
  ];

  static getPlaceholder(tipo, nome = '', options = {}) {
    const {
      width = 300,
      height = 200,
      useIcon = false,
      format = 'webp'
    } = options;

    const config = this.typeConfigs[tipo?.toLowerCase()] || this.typeConfigs.default;
    
    let text = '';
    if (useIcon) {
      text = config.icon;
    } else {
      text = nome ? nome.substring(0, 20) : config.label;
    }
    
    // Gerar SVG local em vez de usar serviço externo
    return this.generateSVGPlaceholder(width, height, config.bg, config.text, text);
  }

  static getAvatarPlaceholder(nome = '', email = '', size = 150) {
    const initials = this.getInitials(nome, email);
    const hash = this.hashString((nome || '') + (email || ''));
    const bgColor = this.avatarColors[hash % this.avatarColors.length];
    
    // Gerar avatar SVG local em vez de chamar boringavatars.com
    return this.generateAvatarSVG(size, initials, bgColor);
  }

  static getInitials(nome, email) {
    // Remove hashtags e símbolos especiais
    const nomeLimpo = nome ? nome.replace(/#[^\s]*/g, '').trim() : '';
    
    if (nomeLimpo) {
      const parts = nomeLimpo.split(/\s+/).filter(p => p.length > 0);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return nomeLimpo.substring(0, 2).toUpperCase();
    }
    
    if (email?.trim()) {
      return email.substring(0, 2).toUpperCase();
    }
    
    return 'US';
  }

  static hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash);
  }

  static isPlaceholderUrl(url) {
    if (!url || typeof url !== 'string') return true;
    
    const placeholderDomains = [
      'placeholder.com',
      'placehold.co',
      'ui-avatars.com',
      'boringavatars.com',
      'via.placeholder',
      'source.boringavatars',
      'data:image/svg+xml' // Agora incluímos nossos SVGs locais
    ];
    
    return placeholderDomains.some(domain => 
      url.toLowerCase().includes(domain.toLowerCase())
    );
  }

  static isValidImageUrl(url) {
    if (!url || typeof url !== 'string') return false;
    
    if (this.isPlaceholderUrl(url)) {
      return true;
    }
    
    try {
      new URL(url);
      return true;
    } catch {
      // Verifica se é data URL
      if (url.startsWith('data:image/')) {
        return true;
      }
      return false;
    }
  }

  static ensureImageUrl(url, tipo, nome = '', fallbackSize = 'medium') {
    if (this.isValidImageUrl(url)) {
      return url;
    }
    
    return this.getFallbackImage(tipo, nome, fallbackSize);
  }

  static getFallbackImage(tipo, nome = '', size = 'medium') {
    const sizes = {
      thumbnail: { width: 100, height: 100 },
      small: { width: 200, height: 150 },
      medium: { width: 400, height: 300 },
      large: { width: 800, height: 600 }
    };
    
    const selectedSize = sizes[size] || sizes.medium;
    return this.getPlaceholder(tipo, nome, selectedSize);
  }

  static getTypeConfig(tipo) {
    return this.typeConfigs[tipo?.toLowerCase()] || this.typeConfigs.default;
  }

  // ============== NOVOS MÉTODOS PARA GERAR SVGs LOCAIS ==============

  /**
   * Gera um placeholder SVG local
   * @param {number} width - Largura
   * @param {number} height - Altura  
   * @param {string} bgColor - Cor de fundo (hex sem #)
   * @param {string} textColor - Cor do texto (hex sem #)
   * @param {string} text - Texto a ser exibido
   * @returns {string} Data URL do SVG
   */
  static generateSVGPlaceholder(width, height, bgColor, textColor, text) {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
        <rect width="100%" height="100%" fill="#${bgColor}" rx="${Math.min(width, height) * 0.05}"/>
        <text 
          x="50%" 
          y="50%" 
          text-anchor="middle" 
          dy="0.35em" 
          font-family="Arial, Helvetica, sans-serif"
          font-size="${Math.min(width, height) * 0.15}" 
          fill="#${textColor}"
          font-weight="bold">
          ${this.escapeHtml(text)}
        </text>
      </svg>
    `;
    
    return `data:image/svg+xml;base64,${btoa(this.minifySVG(svg))}`;
  }

  /**
   * Gera um avatar SVG no estilo "boringavatars"
   * @param {number} size - Tamanho do avatar
   * @param {string} initials - Iniciais (máx 2 caracteres)
   * @param {string} bgColor - Cor de fundo (hex sem #)
   * @returns {string} Data URL do SVG
   */
  static generateAvatarSVG(size, initials, bgColor) {
    const cleanInitials = (initials || '?').substring(0, 2).toUpperCase();
    const textColor = this.getContrastColor(bgColor);
    
    // Cria um gradiente baseado na cor
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <defs>
          <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#${bgColor}99"/>
            <stop offset="100%" stop-color="#${bgColor}"/>
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#grad)" rx="${size * 0.2}"/>
        <text 
          x="50%" 
          y="50%" 
          text-anchor="middle" 
          dy="0.35em" 
          font-family="Arial, Helvetica, sans-serif"
          font-size="${size * 0.4}" 
          fill="#${textColor}"
          font-weight="bold"
          letter-spacing="1">
          ${cleanInitials}
        </text>
      </svg>
    `;
    
    return `data:image/svg+xml;base64,${btoa(this.minifySVG(svg))}`;
  }

  /**
   * Escapa caracteres HTML para uso seguro em SVG
   * @param {string} text - Texto a ser escapado
   * @returns {string} Texto escapado
   */
  static escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Minimiza SVG removendo espaços em branco desnecessários
   * @param {string} svg - Código SVG
   * @returns {string} SVG minimizado
   */
  static minifySVG(svg) {
    return svg
      .replace(/\s+/g, ' ')
      .replace(/>\s+</g, '><')
      .replace(/\s+\/>/g, '/>')
      .trim();
  }

  /**
   * Calcula cor de texto com bom contraste baseado na cor de fundo
   * @param {string} bgColor - Cor de fundo (hex sem #)
   * @returns {string} 'ffffff' para fundos escuros, '333333' para claros
   */
  static getContrastColor(bgColor) {
    // Converte hex para RGB
    const r = parseInt(bgColor.substring(0, 2), 16);
    const g = parseInt(bgColor.substring(2, 4), 16);
    const b = parseInt(bgColor.substring(4, 6), 16);
    
    // Fórmula de luminosidade
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Retorna preto para fundos claros, branco para fundos escuros
    return luminance > 0.5 ? '333333' : 'ffffff';
  }

  /**
   * Cria um placeholder de imagem simples (para compatibilidade)
   * @param {string} text - Texto para placeholder
   * @param {object} options - Opções
   * @returns {string} URL do placeholder
   */
  static simplePlaceholder(text = 'Image', options = {}) {
    const {
      width = 300,
      height = 150,
      bgColor = 'cccccc',
      textColor = '333333'
    } = options;
    
    return this.generateSVGPlaceholder(width, height, bgColor, textColor, text);
  }
}

export default PlaceholderGenerator;